import {
  ExclamationCircleFilled,
  EyeOutlined,
  HistoryOutlined,
  InboxOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Empty, Modal, Popconfirm, Spin, Switch, theme, Tooltip } from 'antd';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowSummary } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { WorkflowCard } from '@/components/WorkflowCard';

import { canArchiveWorkflow } from './canArchive';
import { applyDraftOrder } from './publishedOrder';

/**
 * mode controls which Approved-split this list shows:
 *   - 'published' — Approved && isActive (live for advisors). Drag-to-reorder
 *     stages the canonical Order field (the order BMO ultimately sees), but
 *     only while the parent has put the tab in Rearrange mode — see
 *     `rearranging` below.
 *   - 'approved'  — Approved && !isActive (approved, awaiting the Approver to
 *                   flip Active on so it goes live). Read-only order.
 *
 * Both modes render the same card shell with the same actions; only the filter,
 * the card status badge, the empty-state copy, and drag-enable differ. This is
 * the Approver mirror of the Originator's Published / Pending Publishing tab
 * split.
 *
 * Items + the Publish/Unpublish handler are owned by ApproverDashboard so the
 * two tabs share one source of truth — toggling on Published moves the card
 * to the Approved tab without needing a refresh.
 */
type Props = {
  mode: 'published' | 'approved';
  items: WorkflowSummary[] | null;
  loading: boolean;
  onTogglePublished: (id: string, isActive: boolean) => Promise<void>;
  /** Promote the staged pending-approved version to live. Fires when the
   *  Approver toggles publish on a "Pending Publishing" card whose template
   *  carries a `pendingApprovedVersionNumber`. Distinct from togglePublished
   *  so the BE can demote the prior live version atomically. */
  onPromotePending: (id: string) => Promise<void>;
  /** True while the Approver is in Rearrange mode on this tab. Cards are only
   *  draggable — and only advertise a drag affordance — when this is on.
   *  Reordering re-publishes the form sequence to BMO the moment it saves, so
   *  it stays behind an explicit mode rather than living on every pointer
   *  drag. Always false in 'approved' mode (that tab's order is read-only). */
  rearranging: boolean;
  /** Staged order as a full list of workflow ids, owned by the parent so the
   *  Save button (which lives in the dashboard's tab-bar slot) can commit it.
   *  null = nothing dragged yet, render the canonical order. */
  draftOrder: string[] | null;
  /** Called on each drop with the complete new id order. Stages only — the
   *  parent's Save is what reaches the API. */
  onDraftOrderChange: (ids: string[]) => void;
  /** Current search query — used to switch the empty-state copy between
   *  "tab genuinely empty" and "no matches for search". Empty string means
   *  the search box is empty. */
  searchQuery?: string;
  /** Callback the search-empty state calls to clear the search input. */
  onClearSearch?: () => void;
  /** Retire the workflow. Approved is one of the two archivable statuses. */
  onArchive?: (id: string) => void | Promise<void>;
};

// Stable across renders so useSensors doesn't reinit on every paint. Same 8-px
// activation distance used by the Originator's WorkflowList — keeps a click on
// an action button from triggering a drag.
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } } as const;
// Rearrange mode is a real task the Approver has to complete, so it can't be
// pointer-only (WCAG 2.1.1). dnd-kit's KeyboardSensor makes each card liftable
// with Space and movable with the arrow keys; useSortable's `attributes`
// already supply the role/tabIndex it needs.
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates } as const;

/**
 * Drag-wrap for a single Published card, rendered only while the tab is in
 * Rearrange mode. Based on the Originator's SortablePublishedCard — kept as a
 * local copy since cross-file extraction would tangle two surfaces that may
 * diverge later.
 *
 * The blue ring is the "this card is arrangeable" signal, and `cursor: move`
 * backs it up on hover. Both are scoped to this wrapper, so outside Rearrange
 * mode the card advertises nothing and behaves like every other read-only card
 * on the dashboard.
 */
function SortablePublishedCard({
  id,
  glow,
  children,
}: {
  id: string;
  /** Brand blue, from the AntD theme token — not a literal hex, so the ring
   *  follows the palette when BMO's real tokens land. */
  glow: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        cursor: isDragging ? 'grabbing' : 'move',
        zIndex: isDragging ? 2 : 'auto',
        // Backlight: a soft ring at rest, solid on the card being carried.
        // Radius matches the card's own so the glow tracks its corners.
        boxShadow: isDragging ? `0 0 0 2px ${glow}` : `0 0 0 2px ${glow}40`,
        borderRadius: 8,
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

export function PublishedWorkflows({
  mode,
  items,
  loading,
  onTogglePublished,
  onPromotePending,
  rearranging,
  draftOrder,
  onDraftOrderChange,
  searchQuery = '',
  onClearSearch,
  onArchive,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { oid } = useRoles();
  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );
  const {
    token: { colorPrimary },
  } = theme.useToken();

  if (loading && items === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  // GET /workflows returns the whole template set; filter to the published baseline
  // this tab represents (currentVersionNumber > 0 means the workflow has been approved
  // at least once), then split by isActive.
  //
  // 2026-06-29 staging: a workflow can simultaneously have a live v1 (isActive=true)
  // AND a staged v2 (pendingApprovedVersionNumber>0). Such a workflow appears on
  // BOTH tabs: Published (showing v1) AND Approved Workflows (showing v2). The
  // Approver promotes v2 from the Approved tab via the toggle, which demotes v1
  // and removes both copies from their previous tabs.
  const published = (items ?? []).filter((w) => w.currentVersionNumber > 0);
  const splitForMode =
    mode === 'published'
      ? published.filter((w) => w.isActive)
      : published.filter((w) => !w.isActive || w.pendingApprovedVersionNumber);

  // Overlay the parent's staged order when one exists (only meaningful in
  // 'published' mode). Cards added or removed since the drag started fall
  // through gracefully — see applyDraftOrder for the exact rules.
  const visible = mode === 'published' ? applyDraftOrder(splitForMode, draftOrder) : splitForMode;

  // Drag is live only inside Rearrange mode. Outside it the cards render
  // through the plain grid below, so there are no listeners to fire and no
  // grab affordance to mislead.
  const canReorder = mode === 'published' && rearranging && visible.length > 1;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = visible.findIndex((w) => w.id === active.id);
    const newIdx = visible.findIndex((w) => w.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    // Stage only. The parent's Save button is the single place that reaches
    // the API — a drop must not publish a new sequence to BMO on its own.
    //
    // Search is disabled while rearranging, so `visible` is the whole
    // Published set: the id list handed up is a complete order, and the save
    // path's 0..N-1 renumbering can't clobber a card that was filtered out.
    onDraftOrderChange(arrayMove(visible, oldIdx, newIdx).map((w) => w.id));
  };

  if (visible.length === 0) {
    // Two empty states (INFLOWS-107 AC03):
    //   • Search active + zero matches → "no matches for '<query>'" copy + a
    //     Clear-search button so the reviewer can bail out of a bad query in
    //     one click, mirroring the Originator dashboard's behaviour.
    //   • Search empty + tab empty → keep the original mode-specific copy
    //     that explains WHY the tab is empty for this account.
    const needle = searchQuery.trim();
    return (
      <Empty
        description={
          needle
            ? t('workflow.list.noneMatchSearch', {
                query: needle,
                defaultValue: 'No workflows match "{{query}}" on this tab.',
              })
            : mode === 'published'
              ? t('approver.published.empty')
              : t('approver.approved.empty')
        }
      >
        {needle && onClearSearch && (
          <Button onClick={onClearSearch}>
            {t('workflow.list.clearSearch', { defaultValue: 'Clear search' })}
          </Button>
        )}
      </Empty>
    );
  }

  const card = (w: WorkflowSummary) => {
    // The Approved tab represents "ready to be published next." When the
    // template carries a pendingApprovedVersionNumber the card represents
    // THAT version (v2), and the toggle promotes it (demoting v1 in the
    // process). Otherwise it represents the first-time approval awaiting
    // its initial publish, and the toggle is the regular SetActive flip.
    const isStagedPending = mode === 'approved' && Boolean(w.pendingApprovedVersionNumber);
    const cardVersionNumber = isStagedPending
      ? (w.pendingApprovedVersionNumber as number)
      : w.currentVersionNumber;
    const cardVersionLabel = isStagedPending
      ? (w.pendingApprovedVersionLabel ?? `v${w.pendingApprovedVersionNumber}`)
      : w.currentVersionLabel;
    // AC2 on "Publish an approved request": the user who created the workflow
    // cannot publish (or unpublish) their own submission. BE refuses the call
    // with 403; hiding the toggle here keeps the approver-owner from seeing
    // an affordance they can't use. A different-user approver still sees the
    // toggle normally.
    const isSelfSubmission = !!oid && oid === w.ownerOid;
    // Confirm-copy differs by direction (AC4). Compute once so the
    // ConfirmPopover copy stays close to the branch that fires it.
    const willActivate = isStagedPending || !w.isActive;
    const confirmTitle = willActivate
      ? t('approver.publish.confirmPublishTitle', { defaultValue: 'Publish this workflow?' })
      : t('approver.publish.confirmUnpublishTitle', { defaultValue: 'Unpublish this workflow?' });
    const confirmDesc = willActivate
      ? t('approver.publish.confirmPublishDesc', {
          defaultValue:
            'It will become live for advisors immediately. Recipients you have wired in will be notified.',
        })
      : t('approver.publish.confirmUnpublishDesc', {
          defaultValue: 'Advisors will lose access to this workflow immediately.',
        });
    return (
      <WorkflowCard
        key={`${w.id}-${mode}`}
        workflow={w}
        versionLabel={cardVersionLabel}
        status={w.isActive && mode === 'published' ? 'Published' : 'PendingPublishing'}
        actions={
          <>
            <Tooltip title={t('workflow.builder.preview')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.builder.preview')}
                icon={<EyeOutlined />}
                onClick={() =>
                  // Eye opens THIS card's version preview — the live v1 on
                  // the Published tab, or the staged v2 on the Approved tab.
                  navigate(`/approver/workflows/${w.id}/preview?version=${cardVersionNumber}`)
                }
              />
            </Tooltip>
            <Tooltip title={t('workflow.list.viewDetails')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.list.viewDetails')}
                icon={<ProfileOutlined />}
                onClick={() =>
                  navigate(`/approver/workflows/${w.id}/versions/${cardVersionNumber}`)
                }
              />
            </Tooltip>
            <Tooltip title={t('workflow.list.versions')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.list.versions')}
                icon={<HistoryOutlined />}
                onClick={() => navigate(`/approver/workflows/${w.id}/versions`)}
              />
            </Tooltip>
            {/* Archive — hidden while the workflow is LIVE (advisors are using
                the published version; it has to be unpublished first, per
                2026-08-04: "if the workflow is published and it is under
                published workflows tab, there should be no archive button at
                all") and while it is Pending (archiving would strand it in the
                approval queue).

                The condition comes from the shared predicate rather than being
                spelled out here, and never from `mode`: the Approved tab can
                hold a live v1 carrying a staged v2 (`pendingApprovedVersionNumber`)
                as well as Draft and Pending cards with a published baseline, so
                a tab-based test leaves a button the server rejects. Offering an
                action that always errors is the bug being fixed; don't
                reintroduce it one tab over. */}
            {canArchiveWorkflow(w) && (
              <Popconfirm
                title={t('workflow.archive.archiveTitle', {
                  defaultValue: 'Archive this workflow?',
                })}
                description={t('workflow.archive.archiveConfirm', {
                  defaultValue:
                    'It leaves the dashboard but is not deleted. You can restore it from the Archive page.',
                })}
                okText={t('workflow.archive.archive', { defaultValue: 'Archive' })}
                cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
                onConfirm={() => onArchive?.(w.id)}
              >
                <Tooltip title={t('workflow.archive.archive', { defaultValue: 'Archive' })}>
                  <Button
                    {...iconBtn}
                    aria-label={t('workflow.archive.archive', { defaultValue: 'Archive' })}
                    icon={<InboxOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
            {/* Toggle semantics differ by card type:
                  - 'published' card: deactivate the live workflow.
                  - 'approved' card on first-time pending publish: activate.
                  - 'approved' card with a staged pending: promote v2 → live.
                  On self-submissions per AC2 the switch is replaced with a
                  disabled-info marker so the approver-owner sees WHY they
                  can't act — hiding the whole toggle left them wondering. */}
            {isSelfSubmission ? (
              <Tooltip title={t('approver.publish.selfPublishBlocked')}>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                  <Switch size="small" checked={!isStagedPending && w.isActive} disabled />
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={willActivate ? t('workflow.list.activate') : t('workflow.list.deactivate')}
              >
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                  <Switch
                    size="small"
                    // Fully controlled — the visual state only advances after the
                    // Modal.confirm resolves + the parent's setWorkflows patch
                    // returns. Cancelling the modal leaves the switch as-is.
                    checked={!isStagedPending && w.isActive}
                    onChange={() => {
                      // AC4 on "Publish an approved request": confirmation
                      // prompt before every publish AND unpublish so a single
                      // stray click can't ship a workflow live or take one
                      // down. Distinct copy per direction — see confirmTitle
                      // / confirmDesc above. Modal.confirm blocks until user
                      // decides; onOk fires the actual mutation.
                      Modal.confirm({
                        title: confirmTitle,
                        content: confirmDesc,
                        icon: (
                          <ExclamationCircleFilled style={{ fontSize: 22, color: '#ad6800' }} />
                        ),
                        okText: t('common.yes'),
                        cancelText: t('common.no'),
                        // willActivate implies isStagedPending → promote path,
                        // otherwise fall through to the plain SetActive flip.
                        // (`isStagedPending` is a subset of willActivate, so
                        // ANDing them together was a tautology.)
                        onOk: () =>
                          isStagedPending
                            ? onPromotePending(w.id)
                            : onTogglePublished(w.id, willActivate),
                      });
                    }}
                  />
                </span>
              </Tooltip>
            )}
          </>
        }
      />
    );
  };

  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
  };

  if (!canReorder) {
    return <div style={grid}>{visible.map(card)}</div>;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={visible.map((w) => w.id)} strategy={rectSortingStrategy}>
        <div style={grid}>
          {visible.map((w) => (
            <SortablePublishedCard key={w.id} id={w.id} glow={colorPrimary}>
              {card(w)}
            </SortablePublishedCard>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
